import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Armchair, Ruler, Palette, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReviewsCarousel from '@/components/ReviewsCarousel';


const heroVideos = [
  'https://videos.pexels.com/video-files/4927683/4927683-uhd_2560_1440_30fps.mp4',
  'https://videos.pexels.com/video-files/31985428/13630743_2560_1440_60fps.mp4',
  'https://videos.pexels.com/video-files/7807287/7807287-hd_1920_1080_24fps.mp4',
];

/* ─── Intersection Observer hook for fade-in ─── */
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(el);
      }
    }, { threshold: 0.15, ...options });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

/* ─── Animated wrapper ─── */
const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, isVisible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* ─── Parallax Video Hero ─── */
const ParallaxHero = () => {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const videoRefs = useRef([]);
  const heroRef = useRef(null);
  const parallaxRef = useRef(null);

  // Parallax scroll — write the transform straight to the DOM (no React rerenders) for buttery 60fps scroll.
  useEffect(() => {
    let rafId = 0;
    let ticking = false;
    const apply = () => {
      const el = parallaxRef.current;
      if (el) el.style.transform = `translate3d(0, ${window.scrollY * 0.35}px, 0)`;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(apply);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const advanceVideo = useCallback(() => {
    setFading(true);
    setTimeout(() => {
      setCurrent(prev => (prev + 1) % heroVideos.length);
      setFading(false);
    }, 1200);
  }, []);

  useEffect(() => {
    const vid = videoRefs.current[current];
    if (vid) { vid.currentTime = 0; vid.play().catch(() => {}); }
  }, [current]);

  useEffect(() => {
    const vid = videoRefs.current[current];
    if (!vid) return;
    const onEnded = () => advanceVideo();
    vid.addEventListener('ended', onEnded);
    return () => vid.removeEventListener('ended', onEnded);
  }, [current, advanceVideo]);

  useEffect(() => {
    const timer = setTimeout(advanceVideo, 8000);
    return () => clearTimeout(timer);
  }, [current, advanceVideo]);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax video layer — transform written via ref so we don't re-render on scroll. */}
      <div
        ref={parallaxRef}
        className="absolute inset-0 will-change-transform"
      >
        {heroVideos.map((src, i) => (
          <video
            key={src}
            ref={el => (videoRefs.current[i] = el)}
            muted
            playsInline
            preload={i === 0 ? 'auto' : 'metadata'}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${
              i === current && !fading ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid={`hero-video-${i}`}
          >
            <source src={src} type="video/mp4" />
          </video>
        ))}
      </div>

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-teal-900/50 via-teal-800/30 to-teal-900/60" />

      {/* Hero content - original centered layout */}
      <div className="relative z-10 text-center text-white px-5 max-w-4xl mx-auto" data-testid="hero-content">
        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-display font-semibold leading-[1.05] mb-8 tracking-tight"
          data-testid="hero-title"
        >
          Custom Made Cushions
        </h1>
        <p
          className="text-base sm:text-lg text-white/85 max-w-xl mx-auto mb-12 leading-relaxed font-body"
          data-testid="hero-subtitle"
        >
          Enter your dimensions, choose your fabric, and we'll craft the perfect cushion for your space.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/products">
            <Button
              className="bg-teal-500 text-white hover:bg-teal-600 rounded-full px-10 py-6 text-base font-semibold font-body shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              data-testid="shop-now-btn"
            >
              Shop Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/products">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 py-6 text-base font-semibold font-body transition-all duration-300"
              data-testid="explore-btn"
            >
              Explore Collection
            </Button>
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <div className="w-5 h-8 rounded-full border-2 border-white/40 flex items-start justify-center p-1.5">
          <div className="w-0.5 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
};

/* ─── How It Works Section (light cream bg, intro headline + 4 steps + CTA) ─── */
const HowItWorks = () => {
  const steps = [
    { num: '01', title: 'Choose your style', desc: 'Select from 15+ cushion shapes designed for every space in your home.', Icon: Armchair },
    { num: '02', title: 'Enter dimensions', desc: 'Input your exact length, width, and thickness for a precise custom fit.', Icon: Ruler },
    { num: '03', title: 'Pick your fabric', desc: 'Browse our curated range of premium indoor and outdoor fabrics.', Icon: Palette },
    { num: '04', title: 'Order & relax', desc: 'We handcraft your cushion and deliver it straight to your door.', Icon: PackageCheck },
  ];

  return (
    <section className="py-20 bg-cream" data-testid="how-it-works-section">
      <div className="max-w-[1664px] mx-auto px-6 sm:px-10 lg:px-16">
        <FadeIn>
          <h2
            className="text-4xl sm:text-5xl font-display font-bold text-teal-700 mb-16 tracking-tight max-w-3xl mx-auto text-center leading-[1.1]"
            data-testid="how-it-works-title"
          >
            Custom cushions handcrafted in Melbourne to your exact size.
          </h2>
        </FadeIn>

        {/* Cards grid. The relative wrapper hosts a dashed connector line that runs
            behind the icons on lg+ screens to imply a visual flow between steps. */}
        <div className="relative">
          <div
            className="hidden lg:block absolute left-0 right-0 pointer-events-none"
            style={{
              top: '64px', // aligns with the icon center (card pt-8 + half of 56px circle)
              borderTop: '1.5px dashed rgb(204 224 220)',
              marginLeft: '12.5%',
              marginRight: '12.5%',
            }}
            aria-hidden="true"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {steps.map((step, i) => (
              <FadeIn key={step.num} delay={i * 0.1}>
                <div
                  className="relative bg-white rounded-3xl pt-8 pb-7 px-7 h-full border border-teal-200 shadow-[0_18px_40px_-12px_rgba(15,76,71,0.18)]"
                  data-testid={`step-${i + 1}`}
                >
                  {/* Icon medallion — always teal-filled (active state). */}
                  <div className="relative z-10 mb-6 w-14 h-14 rounded-full bg-teal-600 border border-teal-600 flex items-center justify-center shadow-sm">
                    <step.Icon className="w-6 h-6 text-white" strokeWidth={1.6} />
                  </div>

                  {/* Eyebrow — quietly anchors the step in the sequence. */}
                  <div className="font-body text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-600/70 mb-2">
                    Step {step.num}
                  </div>

                  <h3 className="text-xl font-display font-semibold text-[#0f4c47] mb-2 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-[15px] text-gray-600 font-body leading-relaxed">
                    {step.desc}
                  </p>

                  {/* Accent bar — full active width. */}
                  <div className="mt-5 h-[2px] w-16 bg-teal-600 rounded-full" />
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        <FadeIn delay={0.4}>
          <div className="mt-14 sm:mt-16 flex justify-center">
            <Link
              to="/products/indoor-seat-cushions"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-body font-semibold text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 rounded-full shadow-card hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
              data-testid="how-it-works-build-now-btn"
            >
              Build Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── About Boss Cushions (solid teal bg, white text) ─── */
const AboutBlock = () => {
  return (
    <section className="bg-teal-600" data-testid="about-section">
      <div className="max-w-[1664px] mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <h2
              className="text-4xl sm:text-5xl font-display text-white leading-[1.1] tracking-tight"
              data-testid="about-title"
            >
              Proudly Australian made, from our family to yours.
            </h2>
          </FadeIn>

          <div>
            <FadeIn delay={0.1}>
              <p className="text-white/90 font-body text-lg leading-relaxed mb-6" data-testid="about-text-1">
                <strong className="text-white">We're a family-owned business</strong> based right here in Melbourne. Every cushion is handcrafted in-house at our own factory — no outsourcing, no shortcuts.
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="text-white/80 font-body text-lg leading-relaxed mb-6" data-testid="about-text-2">
                From cutting the foam to sewing the fabric, our team handles every step of the process. That means better quality control and cushions that are made to last.
              </p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <p className="text-white/80 font-body text-lg leading-relaxed" data-testid="about-text-3">
                We deliver nationwide across Australia, so no matter where you are — from Sydney to Perth — your custom cushions will arrive at your door, crafted with care.
              </p>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Shop by Category (heading left, categories right) ─── */
const ShopByCategory = () => {
  const categories = [
    {
      id: 'indoor',
      title: 'Indoor Cushions',
      description: 'Bench seats, chairs, window seats and more — designed for comfort in every room.',
      image: 'https://images.pexels.com/photos/4635231/pexels-photo-4635231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/products/indoor-seat-cushions',
    },
    {
      id: 'outdoor',
      title: 'Outdoor Cushions',
      description: 'Weather-resistant fabrics for patios, decks and alfresco areas.',
      image: 'https://images.pexels.com/photos/27975920/pexels-photo-27975920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/products/outdoor-seat-cushions',
    },
    {
      id: 'sofa',
      title: 'Sofa Cushions',
      description: 'Replacement and custom sofa cushions that bring your furniture back to life.',
      image: 'https://images.unsplash.com/photo-1768332572728-2fff6ecbd6b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpbmRvb3IlMjBsaXZpbmclMjByb29tJTIwY3VzaGlvbnMlMjBjb3p5fGVufDB8fHx8MTc3NTA0OTUyMXww&ixlib=rb-4.1.0&q=85',
      link: '/products/sofa-cushions',
    },
  ];

  return (
    <section className="py-24 sm:py-28 bg-cream" data-testid="categories-section">
      <div className="max-w-[1664px] mx-auto px-6 sm:px-10 lg:px-16">
        <FadeIn>
          <div className="text-center mb-14">
            <h2
              className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-5 tracking-tight leading-[1.1]"
              data-testid="categories-title"
            >
              Cushions for every space, made to measure.
            </h2>
            <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto font-body leading-relaxed mb-8">
              From cozy indoor nooks to outdoor entertaining areas — browse our categories and start building your perfect cushion.
            </p>
            <Link to="/products">
              <Button
                className="bg-teal-500 text-white hover:bg-teal-600 rounded-full px-8 py-5 text-sm font-semibold font-body transition-all duration-300 hover:-translate-y-0.5"
                data-testid="browse-all-btn"
              >
                Browse All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </FadeIn>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, i) => (
            <FadeIn key={cat.id} delay={i * 0.1}>
              <Link
                to={cat.link}
                className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-floating transition-all duration-500 hover:-translate-y-1 h-full"
                data-testid={`category-card-${cat.id}`}
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-display font-semibold text-[#1A1A1A] mb-2">{cat.title}</h3>
                  <p className="text-sm text-gray-500 font-body leading-relaxed mb-4 flex-1">{cat.description}</p>
                  <span className="inline-flex items-center text-teal-600 font-semibold font-body text-sm group-hover:translate-x-1 transition-transform duration-300">
                    Browse Collection <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CTA Section ─── */
const CTASection = () => {
  return (
    <section className="py-24 sm:py-28 bg-cream" data-testid="cta-section">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
        <FadeIn>
          <h2 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-5 tracking-tight">
            Ready to get comfortable?
          </h2>
          <p className="text-base sm:text-lg text-gray-500 mb-10 font-body">
            Start designing your perfect custom cushion today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button
                className="bg-teal-500 text-white hover:bg-teal-600 rounded-full px-10 py-6 text-base font-semibold font-body shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                data-testid="cta-start-designing-btn"
              >
                Start Designing
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/fabric-samples">
              <Button
                variant="outline"
                className="border-gray-300 text-[#1A1A1A] hover:bg-gray-50 rounded-full px-10 py-6 text-base font-semibold font-body transition-all duration-300"
                data-testid="browse-fabrics-btn"
              >
                Browse Fabrics
              </Button>
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── Main HomePage ─── */
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <ParallaxHero />
      <HowItWorks />
      <AboutBlock />
      <ReviewsCarousel />
      <ShopByCategory />
      <CTASection />
    </div>
  );
};

export default HomePage;
